import { animate, style, transition, trigger } from '@angular/animations';
import { Component, OnInit, ChangeDetectionStrategy, Input, OnChanges, SimpleChanges, OnDestroy, HostListener, ViewChild, ElementRef, AfterViewInit, ChangeDetectorRef, HostBinding } from '@angular/core';
import { FormControl, FormControlDirective, ValidationErrors } from '@angular/forms';
import { Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators'
import { componentContainerAnimation, labelAnimation, tickAnimation } from '../animations';
import { Abstract_Form_Component, FormComponent } from '../form-component.types';
import { ScrollService } from '../step/scroll.service';
import { TickService } from '../utilities/tick/tick.service';


@Component({
  selector: 'div[data-component-type=text-input]',
  templateUrl: './text-input.component.html',
  styleUrls: ['./text-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    labelAnimation,
    componentContainerAnimation,
    tickAnimation,
    trigger('prefixAnimation', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease', style({ opacity: 1 }))
      ])
    ])
  ]
})
export class TextInputComponent extends Abstract_Form_Component implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() prefix: string | undefined
  @Input() keyboard!: string
  @Input() maxlength!: number | undefined
  @ViewChild('input') input!: ElementRef<HTMLInputElement>
  @ViewChild(FormControlDirective) controlDirective!: FormControlDirective;

  @HostListener('mouseenter') mouseenter() {
    this.hover = true
    this.labelState = 'displayLabel'
  }
  @HostListener('mouseleave') mouseleave() {
    this.hover = false
    this.updateLabelState()
  }
  @HostListener('focusin') focusin() {
    this.focused = true
    this.prefixState = 'displayPrefix'
    this.labelState = 'displayLabel'
  }
  @HostListener('focusout') focusout() {
    this.focused = false
    this.hover = false
    if (!this.required && !this.controlValueIsNotFalsy) {
      this.formControl.reset()
      this.touchedOrDirty = false
      this.touchedOrDirtyAndInvalid = false
      this.labelState = 'hideLabel'
    } else {
      this.touchedOrDirty = true
      this.touchedOrDirtyAndInvalid = this.formControl.invalid
      this.labelState = 'displayLabel'
    }
    this.updatePrefixState()
  }
  @HostListener('mousedown', ['$event']) mousedown(event: MouseEvent) {
    if (event.target !== this.input.nativeElement && !this.focused) {
      event.stopPropagation()
      event.preventDefault()
      this.input.nativeElement.focus()
    }
  }

  error: string | null = ''
  labelState!: 'displayLabel' | 'hideLabel'
  prefixState!: 'displayPrefix' | 'hidePrefix'
  controlValueIsNotFalsy!: boolean
  touchedOrDirty!: boolean
  touchedOrDirtyAndInvalid!: boolean

  private destroy$: Subject<any> = new Subject()
  @HostBinding('class.hover') private hover: boolean = false

  constructor(scroll: ScrollService, cd: ChangeDetectorRef, private el: ElementRef<HTMLElement>, private tick: TickService) {
    super(cd, scroll)
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled) changes.disabled.currentValue ? this.formControl.enabled && this.formControl.disable() : this.formControl.disabled && this.formControl.enable()
  }

  ngOnInit(): void {
    this.formControl.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.updateControlValueFalsy()
      this.touchedOrDirty = this.formControl.touched || this.formControl.dirty
    })
    this.formControl.statusChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.error = this.formControl.invalid ? Object.keys(this.formControl.errors as ValidationErrors)[0] : null
      this.touchedOrDirtyAndInvalid = this.touchedOrDirty && this.formControl.invalid
    })
    this.scroll.notice$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(path => {
      if (this.formControl.invalid) {
        this.formControl.pristine && this.formControl.markAsDirty()
        this.formControl.untouched && this.formControl.markAsTouched()
        this.touchedOrDirty = true
        this.touchedOrDirtyAndInvalid = true
        this.labelState = 'displayLabel'
      }
      if (path === this.path) {
        this.input.nativeElement.focus()
        this.tick.then(() => this.el.nativeElement.scrollIntoView())
      }
      this.cd.markForCheck()
    })
    this.error = this.formControl.invalid ? Object.keys(this.formControl.errors as ValidationErrors)[0] : null
    this.updateControlValueFalsy()
    this.touchedOrDirty = this.formControl.touched || this.formControl.dirty
    this.touchedOrDirtyAndInvalid = this.formControl.invalid && this.touchedOrDirty
    this.updateLabelState()
    this.updatePrefixState()
  }

  ngAfterViewInit(): void {
    if (this.maxlength !== undefined) this.input.nativeElement.maxLength = this.maxlength
    this.scroll.onInit$.pipe(
      first()
    ).subscribe(path => {
      if (path === this.path) {
        this.input.nativeElement.focus()
        this.tick.then(() => this.el.nativeElement.scrollIntoView())
      }
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next('')
    this.destroy$.complete()
    //@ts-ignore
    this.controlDirective._invokeOnDestroyCallbacks()
  }

  updateLabelState() {
    this.labelState = this.hover || this.focused || this.controlValueIsNotFalsy || this.touchedOrDirty ? 'displayLabel' : 'hideLabel'
  }

  private updatePrefixState() {
    this.prefixState = this.focused || this.controlValueIsNotFalsy ? 'displayPrefix' : 'hidePrefix'
  }

}

export interface TextInputOptions extends FormComponent {
  id: string,
  type: 'text-component',
  prefix: string,
  keyboard: string,
  presence: boolean,
  required: boolean,
  formControl: FormControl,
  maxlength?: number,
}